import { type Dispatch, type SetStateAction, useCallback, useEffect, useState } from "react";

export function useStoredValue<T extends number | string | boolean | object>(
  storageArea: "local" | "session",
  key: string,
  defaultValue: () => T,
): [T, Dispatch<SetStateAction<T>>] {
  const storage = storageArea === "local" ? localStorage : sessionStorage;

  const [value, setValue] = useState<T>(() => {
    const stored = storage.getItem(key);
    if (stored !== null) {
      try {
        return JSON.parse(stored) as T;
      } catch {
        return defaultValue();
      }
    }
    return defaultValue();
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    const stored = storage.getItem(key);
    if (stored !== null) {
      try {
        setValue(JSON.parse(stored));
      } catch {
        setValue(defaultValue());
      }
    }
  }, [key, storage]);

  const setStoredValue = useCallback(
    (newValue: T | ((prev: T) => T)) =>
      setValue((prev) => {
        const nextValue = typeof newValue === "function" ? newValue(prev) : newValue;
        storage.setItem(key, JSON.stringify(nextValue));
        return nextValue;
      }),
    [key, storage],
  );

  return [value, setStoredValue];
}
